import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Edit3, Trash2 } from "lucide-react";
import { api, imgUrl, money, formatApiError } from "../../api";
import ProductForm from "./ProductForm";
import {
  normalizeProductCategoryAssignments,
  productMatchesCategory,
} from "../../utils/productCategoryAssignments";

function ProductPlacements({ product, categories }) {
  const placements = normalizeProductCategoryAssignments(product, categories);
  if (!placements.length) return <span className="category-placement-list">Unassigned</span>;

  return (
    <div className="category-placement-list">
      {placements.map((placement, index) => {
        const childNames = (placement.subcategories || [])
          .map((child) => child.subcategory)
          .filter(Boolean);
        return (
          <span className="category-placement" key={`${placement.category_id || placement.category_slug}-${index}`}>
            <strong>{placement.category || placement.category_slug}</strong>
            {index === 0 && <em className="category-placement-primary">Primary</em>}
            <small>{childNames.length ? childNames.join(", ") : "Main category"}</small>
          </span>
        );
      })}
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/api/products", { params: { limit: 500, include_inactive: true } }),
        api.get("/api/categories/tree", { params: { include_inactive: true } }),
      ]);
      setProducts(p.data);
      setCategories(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = products.filter(
    (p) =>
      (!categoryFilter || productMatchesCategory(p, categoryFilter)) &&
      (!query.trim() || String(p.name || "").toLowerCase().includes(query.trim().toLowerCase())),
  );

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setShowForm(true);
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/api/admin/products/${p.id}/duplicate`);
      toast.success("Product duplicated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <span className="eyebrow">Catalog</span>
          <h1>Products.</h1>
        </div>
        <button className="button dark-button" onClick={openNew} data-testid="admin-new-product-button">
          <Plus size={16} /> New product
        </button>
      </div>

      <div className="admin-toolbar">
        <label className="admin-search">
          <Search size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products"
            data-testid="admin-products-search"
          />
        </label>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          data-testid="admin-products-category-filter"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="empty-state">Loading…</p>
      ) : filtered.length ? (
        <div className="admin-table" data-testid="admin-products-table">
          <div className="admin-table-head admin-products-head">
            <span></span>
            <span>Name</span>
            <span>Category</span>
            <span>Price</span>
            <span>Delivery time</span>
            <span>Flags</span>
            <span></span>
          </div>
          {filtered.map((p) => (
            <div className="admin-table-row admin-products-row" key={p.id} data-testid={`admin-product-row-${p.id}`}>
              <div className="thumb">
                {p.main_image ? <img src={imgUrl(p.main_image)} alt={p.name} /> : <div className="thumb-fallback" />}
              </div>
              <div>
                <strong>{p.name}</strong>
                <span className="eyebrow">{p.look_number}</span>
              </div>
              <ProductPlacements product={p} categories={categories} />
              <span>
                {p.offer_price ? (
                  <>
                    <strong>{money(p.offer_price)}</strong>{" "}
                    <span className="strike">{money(p.original_price)}</span>
                  </>
                ) : (
                  money(p.original_price)
                )}
              </span>
              <span className="delivery-time-cell">
                {p.delivery_time_days || 7} day{Number(p.delivery_time_days || 7) === 1 ? "" : "s"}
              </span>
              <span className="flag-row">
                {p.is_featured && <span className="flag">Featured</span>}
                {p.is_new_arrival && <span className="flag">New</span>}
                {!p.is_active && <span className="flag muted">Hidden</span>}
              </span>
              <div className="row-actions">
                <button onClick={() => openEdit(p)} title="Edit" data-testid={`admin-edit-product-${p.id}`}>
                  <Edit3 size={14} /> Edit
                </button>
                <button onClick={() => duplicate(p)} title="Duplicate" data-testid={`admin-duplicate-product-${p.id}`}>
                  Duplicate
                </button>
                <button onClick={() => remove(p)} title="Delete" data-testid={`admin-delete-product-${p.id}`}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-state">No products.</p>
      )}

      {showForm && (
        <ProductForm
          product={editing}
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
